import type {
  Licence,
  ManagedSubscription,
  SanitizedManagedSubscription,
  SanitizedSelfServeSubscription,
  SanitizedSubscription,
  SelfServeSubscription,
} from '@sine/sine-core-api-types';

import { FOURTEEN_DAYS_IN_MILLISECONDS } from '../../../services/billing/billing.constants';
import type { BadgeInfo, HydratedBillableSite, PlanSelectOption } from '../../../services/billing/billing.types';
import { formatStripeExpiryDate } from './formatStripeDate';

export function getSubscriptionType(subscription: SanitizedSubscription) {
  if (isSelfServeSubscription(subscription)) {
    return subscription.externalData.interval === 'month' ? 'Monthly' : 'Annual';
  } else {
    return 'Managed';
  }
}

function getBadgeInfo(subscriptions: Array<SanitizedSelfServeSubscription>, sites: Array<HydratedBillableSite>) {
  const badgeInfo = subscriptions.reduce((info: Array<BadgeInfo>, subscription) => {
    subscription.licences?.forEach((license) => {
      if (!license.deleted) {
        const { externalData } = license;
        const badgeName = externalData?.plan?.metadata?.badge_name;

        const newBadgeInfoIndex = info.findIndex(
          (item) => item.badgeName === badgeName && item.subscriptionId === subscription.id,
        );

        const matchedSite = sites.find(({ id }) => id === license.site);

        const isUnassigned = !matchedSite || !license.site;

        if (isUnassigned && badgeName) {
          if (newBadgeInfoIndex === -1) {
            info.push({
              badgeName,
              count: 1,
              licenseIds: [license.id],
              subscriptionId: subscription.id,
            });
          } else {
            info[newBadgeInfoIndex] = {
              ...info[newBadgeInfoIndex],
              licenseIds: [...info[newBadgeInfoIndex].licenseIds, license.id],
              count: info[newBadgeInfoIndex].count + 1,
            };
          }
        }
      }
    });

    return info;
  }, []);

  return badgeInfo;
}

function getRenewDateOption(subscription: SanitizedSelfServeSubscription) {
  const { current_period_end, cancel_at_period_end } = subscription.externalData;

  const subscriptionType =
    getSubscriptionType(subscription) === 'Annual' ? 'Annually' : getSubscriptionType(subscription);

  const renewalLabel = cancel_at_period_end
    ? `Expires ${formatStripeExpiryDate(current_period_end)} (No renewal)`
    : `Renews ${formatStripeExpiryDate(current_period_end)} (${subscriptionType}) `;

  return {
    label: renewalLabel,
    value: subscription.id,
  };
}

function getRenewDateOptions(subscriptions: Array<SanitizedSelfServeSubscription>) {
  const renewDateOptions = [];
  if (subscriptions.length) {
    for (const subscription of subscriptions) {
      const renewDateOption = getRenewDateOption(subscription);

      renewDateOptions.push(renewDateOption);
    }
  }

  return renewDateOptions;
}

export function getExistingPlanOptions(
  subscriptions: Array<SanitizedSelfServeSubscription>,
  sites: Array<HydratedBillableSite>,
) {
  const badgeInfo = getBadgeInfo(subscriptions, sites);
  const allOptions = badgeInfo.map(({ badgeName, count, licenseIds, subscriptionId }) => ({
    label: `${badgeName} (${count} remaining)`,
    value: subscriptionId,
    subscriptionId: subscriptionId,
    licenseIds,
    count,
    type: badgeName,
  }));

  const renewDateOptions = getRenewDateOptions(
    subscriptions.filter((subscription) => {
      return badgeInfo.some((info) => info.subscriptionId === subscription.id);
    }),
  );

  return { allOptions, renewDateOptions };
}

function formatManualLicenseName(licenseType: string) {
  const licenseTypeMap: Record<string, string> = {
    small: 'Small plan',
    medium: 'Medium plan',
    enterprise: 'Large plan',
    unlimited: 'Enterprise plan',
  };

  return licenseTypeMap[licenseType];
}

export function getAssignedPlanOption(license: Licence, subscriptions: Array<SanitizedSelfServeSubscription>) {
  const subscription = subscriptions.find((_subscription) => _subscription.id === license.subscription);

  const badgeName = license.externalData?.plan?.metadata.badge_name || formatManualLicenseName(license.type || '');

  const planOption: PlanSelectOption = {
    label: `${badgeName}`,
    value: license.subscription,
    subscriptionId: license.subscription,
    licenseIds: [license.id],
    count: 0,
    type: badgeName,
    siteId: license.site,
  };

  const renewDateOption = getRenewDateOption(subscription!);

  return { planOption, renewDateOption };
}

export function getSysAdminTrialLicenseExpireDate() {
  return `Expires ${formatStripeExpiryDate(new Date(Date.now() + FOURTEEN_DAYS_IN_MILLISECONDS))}`;
}

export function isSelfServeSubscription(
  subscription: SanitizedSubscription,
): subscription is SelfServeSubscription | SanitizedSelfServeSubscription {
  return subscription.type === 'stripe';
}

export function isManagedSubscription(
  subscription: SanitizedSubscription,
): subscription is ManagedSubscription | SanitizedManagedSubscription {
  return subscription.type === 'manual';
}
